import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import LeadSelectionInterface from '../../../../components/LeadSelectionInterface';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';

const steps = [
  'Basic Information',
  'Journey Selection',
  'Schedule Settings',
  'Lead Selection',
  'Test',
  'Review & Activate',
];

// Get list of timezones from moment-timezone
const timezones = moment.tz.names();

const CreateJourneyBasedCampaign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    journey: '',
    campaign_type: 'journey',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    // Retry configuration
    retry_strategy_id: null,
    max_retries: null,
    config: {
      track_opens: true,
      track_clicks: true,
      track_replies: true,
      track_delivery: true,
      allow_opt_out: true,
      opt_out_message: '',
      from_email: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      attachments: [],
      from_number: '',
      media_urls: [],
      voice: 'female',
      language: 'en-US',
      retry_attempts: 3,
      retry_delay: 300,
      record_call: true,
      call_timeout: 60,
      machine_detection: 'prefer_human',
      platform: 'whatsapp',
      quick_replies: []
    },
    journey_schedule: {
      business_hours_only: true,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true,
      exclude_holidays: true,
      min_step_delay: 60,
      max_steps_per_day: 3,
      max_retry_attempts: 3,
      retry_delay_minutes: 60,
      step_timeout_minutes: 1440,
      allow_parallel_steps: false,
      max_parallel_steps: 1,
      timezone: 'UTC',
      is_active: true
    },
    lead_filters: []
  });

  // Test state
  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null,
    selectedStep: '',
  });

  // Load accounts, journeys, and status choices on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsResponse, statusResponse] = await Promise.all([
          api.get('/crm/accounts/'),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);
        setAccounts(accountsResponse.data);
        setStatusChoices(statusResponse.data);
      } catch (err) {
        setError('Failed to load required data');
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, []);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  // Load journeys when account and CRM campaign are selected
  useEffect(() => {
    const fetchJourneys = async () => {
      if (!formData.account || !formData.crm_campaign) {
        setJourneys([]);
        return;
      }

      try {
        const response = await api.get(`/acs/journeys/?account_id=${formData.account}&crm_campaign_id=${formData.crm_campaign}`);
        setJourneys(response.data);
        
        // If current selected journey doesn't belong to new account/campaign, clear it
        if (formData.journey) {
          const journeyExists = response.data.some(j => j.id === formData.journey);
          if (!journeyExists) {
            setFormData(prev => ({
              ...prev,
              journey: ''
            }));
          }
        }
      } catch (err) {
        setError('Failed to load journeys for selected account and campaign');
        console.error('Error loading journeys:', err);
      }
    };
    fetchJourneys();
  }, [formData.account, formData.crm_campaign]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'account') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        journey: '', // Clear journey when account changes
        crm_campaign: '' // Clear campaign when account changes
      }));
    } else if (name === 'journey') {
      const selectedJourney = journeys.find(j => j.id === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        crm_campaign: selectedJourney?.campaign || '',
      }));
    } else if (name.startsWith('journey_schedule.')) {
      const scheduleKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        journey_schedule: {
          ...prev.journey_schedule,
          [scheduleKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: e.target.type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        return formData.name && 
               formData.account && 
               formData.crm_campaign &&
               formData.start_date && 
               (formData.is_ongoing || formData.end_date);
      case 1:
        return formData.journey;
      case 2:
        return (
          formData.journey_schedule.start_time &&
          formData.journey_schedule.end_time &&
          formData.journey_schedule.max_steps_per_day > 0 &&
          formData.journey_schedule.max_retry_attempts > 0 &&
          formData.journey_schedule.retry_delay_minutes > 0 &&
          formData.journey_schedule.step_timeout_minutes > 0 &&
          formData.journey_schedule.timezone &&
          // Retry strategy validation (optional but recommended)
          (formData.retry_strategy_id || formData.max_retries)
        );
      case 3:
        return formData.lead_filters.length > 0;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async (activate = false) => {
    setLoading(true);
    setError(null);

    try {
      // Create the campaign with appropriate status
      const campaignResponse = await api.post('/acs/nurturing-campaigns/', {
        ...formData,
        status: activate ? 'active' : 'draft',
        created_by: token.user_id
      });

      // Only assign leads if the campaign is being activated
      if (activate && formData.lead_filters.length > 0) {
        await api.post(`/acs/nurturing-campaigns/${campaignResponse.data.id}/assign_leads/`, {
          filters: formData.lead_filters,
          status: 'active'
        });
      }

      // If activating, make the activation API call
      if (activate) {
        await api.post(`/acs/nurturing-campaigns/${campaignResponse.data.id}/activate/`);
      }

      navigate('/crm/lead-nurturing');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
      setCreateDialogOpen(false);
    }
  };

  const handleTestJourney = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      const testPayload = {
        journey_id: formData.journey,
        step_id: testData.selectedStep,
        recipient: testData.email || testData.phone
      };
      
      await api.post('/acs/test-journey/', testPayload);
      setTestData(prev => ({ ...prev, testStatus: 'success', testError: null }));
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.message || 'Failed to send test message'
      }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Account</InputLabel>
                <Select
                  name="account"
                  value={formData.account}
                  onChange={handleInputChange}
                  label="Account"
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>CRM Campaign</InputLabel>
                <Select
                  name="crm_campaign"
                  value={formData.crm_campaign}
                  onChange={handleInputChange}
                  label="CRM Campaign"
                  disabled={!formData.account}
                >
                  {crmCampaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Campaign Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Start Date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                error={!formData.start_date}
                helperText={!formData.start_date ? "Start date is required" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formData.is_ongoing}
                required={!formData.is_ongoing}
                error={!formData.is_ongoing && !formData.end_date}
                helperText={!formData.is_ongoing && !formData.end_date ? "End date is required unless campaign is ongoing" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleInputChange}
                    name="active"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_ongoing}
                    onChange={handleInputChange}
                    name="is_ongoing"
                  />
                }
                label="Ongoing Campaign"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                If enabled, the campaign will run indefinitely
              </Typography>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Journey</InputLabel>
                <Select
                  name="journey"
                  value={formData.journey}
                  onChange={handleInputChange}
                  label="Journey"
                >
                  {journeys.map((journey) => (
                    <MenuItem key={journey.id} value={journey.id}>
                      {journey.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Tracking Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.config.track_opens}
                        onChange={handleInputChange}
                        name="config.track_opens"
                      />
                    }
                    label="Track Opens"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.config.track_clicks}
                        onChange={handleInputChange}
                        name="config.track_clicks"
                      />
                    }
                    label="Track Clicks"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.config.track_replies}
                        onChange={handleInputChange}
                        name="config.track_replies"
                      />
                    }
                    label="Track Replies"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.config.track_delivery}
                        onChange={handleInputChange}
                        name="config.track_delivery"
                      />
                    }
                    label="Track Delivery"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.config.allow_opt_out}
                        onChange={handleInputChange}
                        name="config.allow_opt_out"
                      />
                    }
                    label="Allow Opt Out"
                  />
                </Grid>
              </Grid>
            </Grid>
            {formData.config.allow_opt_out && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Opt Out Message"
                  name="config.opt_out_message"
                  value={formData.config.opt_out_message}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Timezone</InputLabel>
                <Select
                  name="journey_schedule.timezone"
                  value={formData.journey_schedule.timezone}
                  onChange={handleInputChange}
                  label="Timezone"
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                name="journey_schedule.start_time"
                value={formData.journey_schedule.start_time}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="time"
                label="End Time"
                name="journey_schedule.end_time"
                value={formData.journey_schedule.end_time}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Step Delay (minutes)"
                name="journey_schedule.min_step_delay"
                value={formData.journey_schedule.min_step_delay}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Steps Per Day"
                name="journey_schedule.max_steps_per_day"
                value={formData.journey_schedule.max_steps_per_day}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Retry Attempts"
                name="journey_schedule.max_retry_attempts"
                value={formData.journey_schedule.max_retry_attempts}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Retry Delay (minutes)"
                name="journey_schedule.retry_delay_minutes"
                value={formData.journey_schedule.retry_delay_minutes}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Step Timeout (minutes)"
                name="journey_schedule.step_timeout_minutes"
                value={formData.journey_schedule.step_timeout_minutes}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.journey_schedule.allow_parallel_steps}
                    onChange={handleInputChange}
                    name="journey_schedule.allow_parallel_steps"
                  />
                }
                label="Allow Parallel Steps"
              />
            </Grid>
            {formData.journey_schedule.allow_parallel_steps && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Parallel Steps"
                  name="journey_schedule.max_parallel_steps"
                  value={formData.journey_schedule.max_parallel_steps}
                  onChange={handleInputChange}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.journey_schedule.business_hours_only}
                    onChange={handleInputChange}
                    name="journey_schedule.business_hours_only"
                  />
                }
                label="Business Hours Only"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.journey_schedule.exclude_weekends}
                    onChange={handleInputChange}
                    name="journey_schedule.exclude_weekends"
                  />
                }
                label="Exclude Weekends"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.journey_schedule.exclude_holidays}
                    onChange={handleInputChange}
                    name="journey_schedule.exclude_holidays"
                  />
                }
                label="Exclude Holidays"
              />
            </Grid>
            
            {/* Retry Configuration */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 2 }}>
                <RetryConfigurationPanel
                  retryStrategy={formData.retry_strategy_id}
                  maxRetries={formData.max_retries}
                  onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
                  onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
                  description="Configure retry behavior for all journey steps"
                />
              </Paper>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Auto-Enrollment Configuration
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_enroll_new_leads}
                      onChange={handleInputChange}
                      name="auto_enroll_new_leads"
                    />
                  }
                  label="Automatically enroll new leads that match the criteria"
                />
                {formData.auto_enroll_new_leads && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    New leads will be automatically enrolled in this journey if they match the filters defined below.
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <LeadSelectionInterface
                accountId={formData.account}
                campaignId={formData.crm_campaign}
                onFiltersChange={(filters) => {
                  setFormData(prev => ({
                    ...prev,
                    lead_filters: filters,
                    auto_enroll_filters: formData.auto_enroll_new_leads ? filters : []
                  }));
                }}
              />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Test Your Journey
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Select a step to test
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Journey Step</InputLabel>
                      <Select
                        value={testData.selectedStep}
                        onChange={(e) => setTestData(prev => ({ ...prev, selectedStep: e.target.value }))}
                        label="Journey Step"
                      >
                        {journeys.find(j => j.id === formData.journey)?.steps?.map((step) => (
                          <MenuItem key={step.id} value={step.id}>
                            {step.name}
                          </MenuItem>
                        )) || []}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Test Email"
                      value={testData.email}
                      onChange={(e) => setTestData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="tel"
                      label="Test Phone"
                      value={testData.phone}
                      onChange={(e) => setTestData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleTestJourney}
                      disabled={testData.testStatus === 'loading' || !testData.selectedStep || (!testData.email && !testData.phone)}
                    >
                      {testData.testStatus === 'loading' ? (
                        <CircularProgress size={24} />
                      ) : (
                        'Send Test'
                      )}
                    </Button>
                  </Grid>
                  {testData.testStatus === 'success' && (
                    <Grid item xs={12}>
                      <Alert severity="success">
                        Test message sent successfully!
                      </Alert>
                    </Grid>
                  )}
                  {testData.testStatus === 'error' && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        {testData.testError}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Campaign Details
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Basic Information</Typography>
                    <Typography>Name: {formData.name}</Typography>
                    <Typography>Description: {formData.description}</Typography>
                    <Typography>
                      Account: {accounts.find((a) => a.id === formData.account)?.name}
                    </Typography>
                    <Typography>
                      Journey: {journeys.find((j) => j.id === formData.journey)?.name}
                    </Typography>
                    <Typography>
                      Start Date: {formData.start_date || 'Not set'}
                    </Typography>
                    <Typography>
                      End Date: {formData.is_ongoing ? 'Ongoing' : (formData.end_date || 'Not set')}
                    </Typography>
                    <Typography>Active: {formData.active ? 'Yes' : 'No'}</Typography>
                    <Typography>Status: {formData.status}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Tracking Configuration</Typography>
                    <Typography>Track Opens: {formData.config.track_opens ? 'Yes' : 'No'}</Typography>
                    <Typography>Track Clicks: {formData.config.track_clicks ? 'Yes' : 'No'}</Typography>
                    <Typography>Track Replies: {formData.config.track_replies ? 'Yes' : 'No'}</Typography>
                    <Typography>Track Delivery: {formData.config.track_delivery ? 'Yes' : 'No'}</Typography>
                    <Typography>Allow Opt Out: {formData.config.allow_opt_out ? 'Yes' : 'No'}</Typography>
                    {formData.config.allow_opt_out && (
                      <Typography>Opt Out Message: {formData.config.opt_out_message}</Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Schedule Settings</Typography>
                    <Typography>Timezone: {formData.journey_schedule.timezone}</Typography>
                    <Typography>
                      Time Window: {formData.journey_schedule.start_time} - {formData.journey_schedule.end_time}
                    </Typography>
                    <Typography>
                      Min Step Delay: {formData.journey_schedule.min_step_delay} minutes
                    </Typography>
                    <Typography>
                      Max Steps Per Day: {formData.journey_schedule.max_steps_per_day}
                    </Typography>
                    <Typography>
                      Max Retry Attempts: {formData.journey_schedule.max_retry_attempts}
                    </Typography>
                    <Typography>
                      Retry Delay: {formData.journey_schedule.retry_delay_minutes} minutes
                    </Typography>
                    <Typography>
                      Step Timeout: {formData.journey_schedule.step_timeout_minutes} minutes
                    </Typography>
                    <Typography>
                      Allow Parallel Steps: {formData.journey_schedule.allow_parallel_steps ? 'Yes' : 'No'}
                    </Typography>
                    {formData.journey_schedule.allow_parallel_steps && (
                      <Typography>
                        Max Parallel Steps: {formData.journey_schedule.max_parallel_steps}
                      </Typography>
                    )}
                    <Typography>
                      Business Hours Only: {formData.journey_schedule.business_hours_only ? 'Yes' : 'No'}
                    </Typography>
                    <Typography>
                      Exclude Weekends: {formData.journey_schedule.exclude_weekends ? 'Yes' : 'No'}
                    </Typography>
                    <Typography>
                      Exclude Holidays: {formData.journey_schedule.exclude_holidays ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Lead Selection</Typography>
                    <Typography>
                      Number of Filters: {formData.lead_filters.length}
                    </Typography>
                    {formData.lead_filters.map((filter, index) => (
                      <Typography key={index} sx={{ ml: 2 }}>
                        • {filter.model} - {filter.field} {filter.operator} {filter.value}
                      </Typography>
                    ))}
                    <Typography sx={{ mt: 2 }}>
                      Auto-Enrollment: {formData.auto_enroll_new_leads ? 'Enabled' : 'Disabled'}
                    </Typography>
                    {formData.auto_enroll_new_leads && (
                      <>
                        <Typography>Auto-Enrollment Criteria:</Typography>
                        {formData.auto_enroll_filters.map((filter, index) => (
                          <Typography key={index} sx={{ ml: 2 }}>
                            • {filter.model} - {filter.field} {filter.operator} {filter.value}
                          </Typography>
                        ))}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/crm/lead-nurturing/create')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom>
            Create Journey-Based Campaign
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading || !validateStep()}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateStep()}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Create Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you want to create this campaign:
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSubmit(false)} color="primary">
            Create as Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} color="success" variant="contained">
            Create & Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateJourneyBasedCampaign; 